import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import { UserProfile, FleetSettings } from '@/types'

// Helper function to convert undefined and empty string values to null for Firestore
// This allows clearing optional fields by passing undefined or empty string
const cleanUndefinedValues = (obj: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {}
  for (const [key, value] of Object.entries(obj)) {
    // Convert both undefined and empty string to null to allow clearing fields
    cleaned[key] = (value === undefined || value === '') ? null : value
  }
  return cleaned
}

// Convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate()
  }
  return new Date()
}

export const userProfileService = {
  // Get user profile from Firestore
  getUserProfile: async (uid: string): Promise<UserProfile | null> => {
    try {
      const docRef = doc(db, 'users', uid)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        return {
          uid,
          email: data.email,
          displayName: data.displayName,
          companyName: data.companyName,
          organizationNumber: data.organizationNumber,
          phoneNumber: data.phoneNumber,
          address: data.address,
          city: data.city,
          postalCode: data.postalCode,
          profileCompleted: data.profileCompleted || false,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt),
          // Åkeri settings with defaults
          driverRevenueShare: data.driverRevenueShare ?? 40,
          vacationPayRate: data.vacationPayRate ?? 12.5,
          taxRate: data.taxRate ?? 30,
          vatRate: data.vatRate ?? 6,
          feeOnTips: data.feeOnTips ?? true,
          taxCalculationMethod: data.taxCalculationMethod ?? 'fixed',
          taxTableName: data.taxTableName ?? 'skattetabell31',
          taxColumn: data.taxColumn ?? 1,
          employerTaxRate: data.employerTaxRate ?? 31.42,
          includeSalaryCalculationPage: data.includeSalaryCalculationPage ?? true,
          revenueShareMode: data.revenueShareMode,
          revenueShareTiers: data.revenueShareTiers,
          salaryEmailTemplate: data.salaryEmailTemplate,
          featureFlags: data.featureFlags
        }
      }
      
      return null
    } catch (error) {
      console.error('Error getting user profile:', error)
      throw new Error('Kunde inte hämta användarprofil')
    }
  },

  // Create initial user profile after registration
  createUserProfile: async (user: User, companyName?: string): Promise<void> => {
    try {
      const userRef = doc(db, 'users', user.uid)
      
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'Användare',
        companyName: companyName || '',
        organizationNumber: null,
        phoneNumber: null,
        address: null,
        city: null,
        postalCode: null,
        profileCompleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Default åkeri settings for new users
        driverRevenueShare: 40,
        vacationPayRate: 12.5,
        taxRate: 30,
        vatRate: 6,
        feeOnTips: 'fleet_commission', // Use new enum value for new users
        taxCalculationMethod: 'fixed',
        taxTableName: 'skattetabell31',
        taxColumn: 1,
        includeSalaryCalculationPage: true,  // Include detailed calculation page in salary specifications
        revenueShareMode: 'single',  // Default to single rate mode
        revenueShareTiers: null      // No tiers by default
      })
    } catch (error) {
      console.error('Error creating user profile:', error)
      throw new Error('Kunde inte skapa användarprofil')
    }
  },

  // Update full user profile
  updateUserProfile: async (uid: string, profile: Partial<UserProfile>): Promise<void> => {
    try {
      const userRef = doc(db, 'users', uid)
      
      // Remove uid and timestamps from update data
      const { uid: _, createdAt, updatedAt, ...rawUpdateData } = profile
      
      // Clean undefined values for Firestore
      const updateData = cleanUndefinedValues({
        ...rawUpdateData,
        updatedAt: serverTimestamp()
      })
      
      await updateDoc(userRef, updateData)
    } catch (error) {
      console.error('Error updating user profile:', error)
      throw new Error('Kunde inte uppdatera användarprofil')
    }
  },

  // Update åkeri settings
  updateFleetSettings: async (uid: string, settings: FleetSettings): Promise<void> => {
    try {
      // Validate settings
      const errors: string[] = []
      
      if (typeof settings.driverRevenueShare !== 'number' || 
          settings.driverRevenueShare < 0 || 
          settings.driverRevenueShare > 100) {
        errors.push('Driver revenue share must be between 0 and 100')
      }
      
      if (typeof settings.vacationPayRate !== 'number' || 
          settings.vacationPayRate < 0 || 
          settings.vacationPayRate > 50) {
        errors.push('Vacation pay rate must be between 0 and 50')
      }
      
      if (typeof settings.taxRate !== 'number' || 
          settings.taxRate < 0 || 
          settings.taxRate > 100) {
        errors.push('Tax rate must be between 0 and 100')
      }
      
      if (typeof settings.vatRate !== 'number' ||
          settings.vatRate < 0 ||
          settings.vatRate > 25) {
        errors.push('VAT rate must be between 0 and 25')
      }

      if (typeof settings.feeOnTips !== 'boolean' &&
          !['fleet_commission', 'driver_share', 'separate_untaxed'].includes(settings.feeOnTips as string)) {
        errors.push('Fee on tips must be a TipsHandlingMode or boolean value')
      }

      if (errors.length > 0) {
        throw new Error(`Validation failed: ${errors.join(', ')}`)
      }
      
      const userRef = doc(db, 'users', uid)
      
      const updateData = cleanUndefinedValues({
        driverRevenueShare: settings.driverRevenueShare,
        vacationPayRate: settings.vacationPayRate,
        taxRate: settings.taxRate,
        vatRate: settings.vatRate,
        feeOnTips: settings.feeOnTips,
        taxCalculationMethod: settings.taxCalculationMethod,
        taxTableName: settings.taxTableName,
        taxColumn: settings.taxColumn,
        employerTaxRate: settings.employerTaxRate,
        includeSalaryCalculationPage: settings.includeSalaryCalculationPage,
        revenueShareMode: settings.revenueShareMode,
        revenueShareTiers: settings.revenueShareTiers,
        salaryEmailTemplate: settings.salaryEmailTemplate,
        updatedAt: serverTimestamp()
      })
      
      await updateDoc(userRef, updateData)
    } catch (error) {
      console.error('Error updating åkeri settings:', error)
      throw new Error(error instanceof Error ? error.message : 'Kunde inte uppdatera flottinställningar')
    }
  }
}